import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import SearchBox from "@/components/feature/SearchBox";
import { categories } from "@/mocks/categories";

const mainNav = [
  { label: "首页", to: "/", icon: "ri-home-5-line", activeIcon: "ri-home-5-fill" },
  { label: "分类", to: "/category/adventure", icon: "ri-apps-2-line", activeIcon: "ri-apps-2-fill", match: "/category" },
  { label: "筛选", to: "/list", icon: "ri-filter-3-line", activeIcon: "ri-filter-3-fill" },
  { label: "关于", to: "/about", icon: "ri-information-line", activeIcon: "ri-information-fill" },
];

const SideNav = () => {
  const location = useLocation();
  const { favorites } = useFavorites();
  const isActive = (item: { to: string; match?: string }) => {
    if (item.match) return location.pathname.startsWith(item.match);
    if (item.to === "/") return location.pathname === "/";
    return location.pathname.startsWith(item.to);
  };

  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[248px] flex-col border-r border-background-200 bg-background-100 px-4 py-6">
      <Link to="/" className="flex items-center gap-3 px-2">
        <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
          <i className="ri-movie-2-fill text-xl" />
        </span>
        <span className="leading-tight">
          <span className="block font-heading text-[17px] font-bold text-foreground-950 whitespace-nowrap">动漫免费在观看</span>
          <span className="block text-[11px] text-foreground-500 tracking-wide">ANIME FREE WATCH</span>
        </span>
      </Link>

      <div className="mt-6">
        <SearchBox compact />
      </div>

      <nav className="mt-6 flex flex-col gap-1" aria-label="主导航">
        {mainNav.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
              isActive(item)
                ? "bg-primary-500 text-background-50"
                : "text-foreground-700 hover:bg-background-200"
            }`}
          >
            <i className={`${isActive(item) ? item.activeIcon : item.icon} text-lg`} />
            <span className="whitespace-nowrap">{item.label}</span>
          </Link>
        ))}
        <Link
          to="/favorites"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
            location.pathname.startsWith("/favorites")
              ? "bg-primary-500 text-background-50"
              : "text-foreground-700 hover:bg-background-200"
          }`}
        >
          <i className={`${location.pathname.startsWith("/favorites") ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`} />
          <span className="whitespace-nowrap flex-1">我的收藏</span>
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-background-300 text-foreground-700">
            {favorites.length}
          </span>
        </Link>
      </nav>

      <div className="mt-6">
        <p className="px-3 text-[11px] font-semibold tracking-widest text-foreground-500">热门分类</p>
        <nav className="mt-2 grid grid-cols-2 gap-1.5" aria-label="分类快捷入口">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-[13px] text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors cursor-pointer"
            >
              <i className={`${cat.icon} text-base`} />
              <span className="whitespace-nowrap">{cat.name}</span>
            </Link>
          ))}
        </nav>
      </div>

      <div className="mt-auto rounded-lg bg-background-50 border border-background-200 p-3">
        <p className="text-[12px] text-foreground-600 leading-relaxed">
          本站所有集数列表为<strong className="text-foreground-900">演示界面</strong>，暂不提供真实视频播放。
        </p>
      </div>
    </aside>
  );
};

const MobileTopBar = () => (
  <div className="lg:hidden sticky top-0 z-40 bg-background-50/95 backdrop-blur border-b border-background-200">
    <div className="flex items-center justify-between px-4 h-14">
      <Link to="/" className="flex items-center gap-2 cursor-pointer">
        <span className="w-8 h-8 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
          <i className="ri-movie-2-fill text-base" />
        </span>
        <span className="font-heading text-[15px] font-bold whitespace-nowrap">动漫免费在观看</span>
      </Link>
      <Link
        to="/search"
        aria-label="搜索动漫"
        className="w-9 h-9 rounded-full bg-background-100 text-foreground-800 flex items-center justify-center cursor-pointer"
      >
        <i className="ri-search-line text-lg" />
      </Link>
    </div>
  </div>
);

const MobileBottomNav = () => {
  const location = useLocation();
  const { favorites } = useFavorites();
  const items = [
    { label: "首页", to: "/", icon: "ri-home-5-line", activeIcon: "ri-home-5-fill" },
    { label: "分类", to: "/category/adventure", icon: "ri-apps-2-line", activeIcon: "ri-apps-2-fill", match: "/category" },
    { label: "搜索", to: "/search", icon: "ri-search-line", activeIcon: "ri-search-fill", match: "/search" },
    { label: "收藏", to: "/favorites", icon: "ri-heart-3-line", activeIcon: "ri-heart-3-fill", badge: favorites.length },
    { label: "更多", to: "/about", icon: "ri-more-2-line", activeIcon: "ri-more-2-fill", match: "/about" },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50 border-t border-background-200"
      aria-label="移动端导航"
    >
      <div className="grid grid-cols-5">
        {items.map((item) => {
          const active = item.match
            ? location.pathname.startsWith(item.match)
            : item.to === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`relative flex flex-col items-center justify-center gap-1 py-2.5 cursor-pointer ${
                active ? "text-primary-600" : "text-foreground-500"
              }`}
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className={`${active ? item.activeIcon : item.icon} text-xl`} />
                {item.badge ? (
                  <span className="absolute top-1.5 right-[26%] min-w-[15px] h-[15px] px-1 rounded-full bg-accent-500 text-background-50 text-[9px] leading-[15px] text-center">
                    {item.badge}
                  </span>
                ) : null}
              </span>
              <span className="text-[11px]">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname, search]);
  return null;
};

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <SideNav />
      <MobileTopBar />
      <div className="lg:pl-[248px]">
        <main className={`min-h-screen pb-20 lg:pb-0 ${ready ? "animate-fade-up" : "opacity-0"}`}>{children}</main>
      </div>
      <MobileBottomNav />
    </div>
  );
}